import { useMemo } from 'react';
import type {
  CampaignPurpose,
  CampaignTemplateVariableConfig,
  CampaignTemplateVariableSource,
  WhatsAppTemplate,
} from '@workspace/types';
import {
  CAMPAIGN_TEMPLATE_VARIABLE_SOURCE_LABELS,
  COBRANZAS_TEMPLATE_VARIABLE_SOURCES,
  CONTACT_TEMPLATE_VARIABLE_SOURCES,
} from '@workspace/utils';
import { Label } from '@/components/ui/label';
import {
  buildDefaultTemplateMapping,
  defaultSourceForSlot,
  mergeTemplateVariableConfig,
  resolveTemplateSlots,
  sanitizeTemplateMappingForPurpose,
} from './ContactAgentTemplateVariableMapper.utils';

type Props = {
  templateName: string;
  templates: WhatsAppTemplate[];
  purpose: CampaignPurpose;
  value: CampaignTemplateVariableConfig;
  onChange: (value: CampaignTemplateVariableConfig) => void;
};

export function ContactAgentTemplateVariableMapper({
  templateName,
  templates,
  purpose,
  value,
  onChange,
}: Props) {
  const boundedName = templateName.trim().slice(0, 128);
  const template = useMemo(
    () => templates.find(item => item.name === boundedName),
    [templates, boundedName],
  );
  const slots = useMemo(
    () => resolveTemplateSlots(boundedName, templates, purpose),
    [boundedName, templates, purpose],
  );
  const sources =
    purpose === 'COBRANZAS'
      ? COBRANZAS_TEMPLATE_VARIABLE_SOURCES
      : CONTACT_TEMPLATE_VARIABLE_SOURCES;

  if (!boundedName || slots.length === 0) {
    return null;
  }

  const mapping = sanitizeTemplateMappingForPurpose(
    value[boundedName] ?? buildDefaultTemplateMapping(slots, purpose),
    slots,
    purpose,
  );

  return (
    <div className='space-y-3 rounded-xl border bg-muted/20 p-4'>
      <div>
        <p className='text-sm font-medium'>Variables de plantilla</p>
        <p className='text-xs text-muted-foreground'>
          Mapea cada placeholder de Meta ({'{{1}}'}, {'{{2}}'}, …) al dato del contacto o deuda.
        </p>
      </div>
      {slots.map(slot => {
        const key = String(slot);
        return (
          <div key={key} className='grid gap-2 md:grid-cols-[120px_1fr] md:items-center'>
            <Label htmlFor={`template-var-${boundedName}-${key}`}>{`{{${slot}}}`}</Label>
            <select
              id={`template-var-${boundedName}-${key}`}
              className='flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm'
              value={mapping[key] ?? defaultSourceForSlot(slot, purpose)}
              onChange={event => {
                const nextSource = event.target.value as CampaignTemplateVariableSource;
                onChange(
                  mergeTemplateVariableConfig(value, boundedName, {
                    ...mapping,
                    [key]: nextSource,
                  }),
                );
              }}
            >
              {sources.map(source => (
                <option key={source} value={source}>
                  {CAMPAIGN_TEMPLATE_VARIABLE_SOURCE_LABELS[source]}
                </option>
              ))}
            </select>
          </div>
        );
      })}
      {!template ? (
        <p className='text-xs text-muted-foreground'>
          Plantilla no sincronizada: se muestran slots por defecto. Sincroniza en Admin → WhatsApp
          para detectar variables automaticamente.
        </p>
      ) : null}
    </div>
  );
}
